import { createRouter, createWebHashHistory } from 'vue-router';
import { computed, watch } from 'vue';
import useUiModalsStore from '@/stores/ui/modals';
import useUsersStore from '@/stores/users';
import clientConfig from '@/client_customs/config';
import Navigation from '@/components/Navigation/Navigation';
import Home from '@/components/Home/Home';
import Planning from '@/components/Planning/Planning';
import PlanningUsers from '@/components/Dashboards/PlanningUsers/Planningusers';
import ProgressDashboard from '@/components/Dashboards/Progress/ProgressDashboard';
import ActionsDashboard from '@/components/Dashboards/Actions/ActionsDashboard';
import ProcessDashboard from '@/components/Dashboards/Process/ProcessDashboard';
import Portfolio from '@/components/Dashboards/Portfolio/Portfolio';
import Manage from '@/components/Manage/Manage';
import PlanningVersions from '@/components/Dashboards/PlanningVersions/PlanningVersions';

const PersonalCalendar = () => import('@/components/Dashboards/PersonalCalendar/PersonalCalendar'); // lazy load fullcalendar dependency
const MonitoringPlan = () => import('@/components/MonitoringPlan/MonitoringPlan');
const MicrosoftTeamsConfig = () => import('@/components/Integrations/MicrosoftTeams/MicrosoftTeamsConfig');
const MicrosoftTeamsDeletion = () => import('@/components/Integrations/MicrosoftTeams/MicrosoftTeamsDeletion');

function loginRequired() { // use when no api call triggers login window
  const usersStore = useUsersStore();
  if (usersStore.isAnonymous === false) return true; // loggued in
  if (usersStore.isAnonymous) { // anonymous
    useUiModalsStore().setLoginWindow(true);
    return new Promise((resolve) => { setTimeout(() => resolve(true), 300); }); // wait for login window to be displayed
  }
  const isAnonymous = computed(() => usersStore.isAnonymous);
  watch(isAnonymous, (newVal) => {
    if (newVal) useUiModalsStore().setLoginWindow(true);
  }, { once: true });
  return true;
}

const router = createRouter({
  history: createWebHashHistory(),
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
  routes: [
    {
      path: '/auth_token/:auth_token',
      beforeEnter(to, from, next) {
        const token = to.params.auth_token;
        if (token) {
          window.apiSrv.createSessionFromToken(token).then(() => {
            window.location.href = "";
          }).catch(() => next('/'));
        } else {
          next('/');
        }
      },
      component: { render: () => null },
    },
    {
      path: '/home',
      components: { default: Home, navigation: Navigation },
      props: { default: { section: 'home' }, navigation: { config: 'home' } },
    },
    {
      path: '/projects',
      components: { default: Home, navigation: Navigation },
      props: { default: { section: 'projects' }, navigation: { config: 'home', section: 'projects' } },
    },
    {
      path: '/dashboards',
      components: { default: Home, navigation: Navigation },
      props: { default: { section: 'dashboards' }, navigation: { config: 'home', section: 'dashboards' } },
      beforeEnter: loginRequired,
    },

    { path: '/planning/:id/:prettyname?', name: 'Planning', component: Planning, props: { dynamic: true } },
    { path: '/viewer/:id/:prettyname?', name: 'PlanningViewer', component: Planning, props: {} },
    { path: '/planningview/:id/:prettyname?', component: Planning, props: { dynamic: true, view: true } },
    { path: '/mobilewebview/:id', component: Planning, props: {} },

    {
      path: '/analysis/planningusers',
      component: PlanningUsers,
      beforeEnter: (to, from, next) => {
        loginRequired();
        if (from.path == '/analysis/process') {
          window.localStorageWrapper.setItem('planningUsersKanbanMode', '');
          next();
        } else if (window.localStorageWrapper.getItem('planningUsersKanbanMode')) {
          next({ path: '/analysis/process', replace: true });
        } else {
          next();
        }
      },
    },
    {
      path: '/analysis/process',
      component: ProcessDashboard,
      beforeEnter: (to, from, next) => {
        loginRequired();
        window.localStorageWrapper.setItem('planningUsersKanbanMode', 1);
        next();
      },
    },
    { path: '/analysis/progress', component: ProgressDashboard, beforeEnter: loginRequired },
    { path: '/analysis/actions', component: ActionsDashboard, beforeEnter: loginRequired },
    { path: '/analysis/portfolio', component: Portfolio, beforeEnter: loginRequired },
    { path: '/analysis/personalcalendar', component: PersonalCalendar, beforeEnter: loginRequired },
    { path: '/analysis/planningversions/:id/:prettyname?', component: PlanningVersions },

    {
      path: '/manage',
      components: { default: Manage, navigation: Navigation },
      props: { navigation: { config: 'manage' } },
    },

    {
      path: '/dashboards/monitoringplan',
      components: { default: MonitoringPlan, navigation: Navigation },
      props: { navigation: { config: 'manage' } },
    },
    ...(clientConfig.dashboards?.router || []),

    /* Herblay specifics */
    { path: '/dashboards/herblay/eventform', component: () => import(/* webpackChunkName: "herblay" */ '@/client_customs/Herblay/HerblayEventSubmission') },
    { path: '/dashboards/herblay/eventform/admin/:eventId?', component: () => import(/* webpackChunkName: "herblay" */ '@/client_customs/Herblay/HerblayEventAdmin') },

    /* Locabri specifics */
    { path: '/dashboards/locabri/import', component: () => import(/* webpackChunkName: "locabri" */ '@/client_customs/Locabri/LocabriImport') },

    { path: '/integrations/microsoftteams/config', component: MicrosoftTeamsConfig },
    { path: '/integrations/microsoftteams/deletion', component: MicrosoftTeamsDeletion },

    { path: '/id_token:catchAll(.*)', component: Home, props: { section: 'home' } }, // keep openid id_token in url (protocol jwt)
    { path: '/:catchAll(.*)', redirect: '/home' },
  ],
});

router.beforeEach((to, from, next) => {
  // Mobile api token
  const { mobiletoken } = to.query;
  if (mobiletoken) {
    window.apiSrv.setAuthToken(mobiletoken, 'mobileapp');
  }

  // mobileview
  if (to.query.mobileview || clientConfig.mobileview) {
    useUiModalsStore().mobileview = true;
    document.body.classList.add('mobileview');
  }

  if (clientConfig.router?.beforeEach) {
    clientConfig.router.beforeEach(to, from, next);
    return;
  }

  next();
});

export default router;
